import { apiFetch } from "@/lib/api";

export type Employee = {
  id: number;
  name: string;
  phone: string;
  store: string;
  department?: string | null;
  position?: string | null;
  status: string;
  monthlyLimit?: number | null;
  discountRate?: number | null;
  hireDate?: string | null;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UsageRow = {
  id: number;
  employeeId: number;
  employeeName: string;
  employeePhone: string;
  store: string;
  amount: number;
  purchaseAmount?: number | null;
  monthKey: string;
  note?: string | null;
  createdAt: string;
  status?: string;
  cancelledAt?: string | null;
};

export type EmployeeContribution = {
  employeeId: number;
  employeeName: string;
  store: string;
  discountTotal: number;
  purchaseTotal: number;
  paymentTotal: number;
  count: number;
};

export type CheckResult = {
  ok: boolean;
  reason: "OK" | "NOT_FOUND" | "INACTIVE" | "LIMIT_EXCEEDED" | string;
  employee?: Employee | null;
  limit?: number;
  used?: number;
  remaining?: number;
  monthKey?: string;
  discountRate?: number;
};

export type ApplyResult = {
  ok: boolean;
  reason: string;
  employee?: Employee;
  appliedAmount?: number;
  used?: number;
  remaining?: number;
  limit?: number;
  check?: CheckResult;
};

export type Stats = {
  monthKey: string;
  totalUsed: number;
  totalPurchase: number;
  totalActive: number;
  totalLimit: number;
  storeBreakdown: { store: string; total: number; purchaseTotal?: number }[];
  limit: number;
  employeeContributions: EmployeeContribution[];
};

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json();
  if (!json.data && json.ok === false) throw new Error(json.error?.message ?? "오류 발생");
  return json.data as T;
}

export async function fetchCheck(phone: string): Promise<CheckResult> {
  const res = await apiFetch(`/discount/check?phone=${encodeURIComponent(phone)}`, { auth: false });
  return unwrap<CheckResult>(res);
}

export async function fetchApply(params: { phone: string; amount: number; purchaseAmount?: number; store: string; note?: string }): Promise<ApplyResult> {
  const res = await apiFetch("/discount/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
    auth: false,
  });
  return unwrap<ApplyResult>(res);
}

export async function fetchAvailableMonths(): Promise<string[]> {
  const res = await apiFetch("/discount/months", { auth: false });
  const d = await unwrap<{ months: string[] }>(res);
  return d.months;
}

export async function fetchEmployees(store?: string, status?: string): Promise<Employee[]> {
  const params = new URLSearchParams();
  if (store) params.set("store", store);
  if (status) params.set("status", status);
  const q = params.toString();
  const res = await apiFetch(`/employees${q ? "?" + q : ""}`, { auth: false });
  const d = await unwrap<{ employees: Employee[] }>(res);
  return d.employees;
}

export async function exportEmployees(store?: string, status?: string): Promise<void> {
  const params = new URLSearchParams();
  if (store) params.set("store", store);
  if (status) params.set("status", status);
  const res = await apiFetch(`/employees/export?${params.toString()}`, { auth: true });
  if (!res.ok) throw new Error("직원목록 내보내기 실패");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const storeLabel = store || "전체매장";
  const statusLabel = status === "active" ? "재직중" : status === "inactive" ? "비활성" : "전체";
  a.href = url;
  a.download = `직원목록_${storeLabel}_${statusLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function createEmployee(data: Omit<Employee, "id" | "status" | "createdAt" | "updatedAt">): Promise<Employee> {
  const res = await apiFetch("/employees", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    auth: false,
  });
  const d = await unwrap<{ employee: Employee }>(res);
  return d.employee;
}

export async function bulkUpsert(employees: { name: string; phone: string; store: string; hireDate?: string; note?: string }[]) {
  const res = await apiFetch("/employees/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ employees }),
    auth: false,
  });
  return unwrap<{ results: { created: number; updated: number; errors: string[] } }>(res);
}

export async function updateEmployee(id: number, data: Partial<Employee>): Promise<Employee> {
  const res = await apiFetch(`/employees/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    auth: false,
  });
  const d = await unwrap<{ employee: Employee }>(res);
  return d.employee;
}

export async function deactivateEmployee(id: number): Promise<Employee> {
  const res = await apiFetch(`/employees/${id}`, { method: "DELETE", auth: false });
  const d = await unwrap<{ employee: Employee }>(res);
  return d.employee;
}

export async function purgeEmployee(id: number): Promise<{ ok: boolean }> {
  const res = await apiFetch(`/employees/${id}/permanent`, { method: "DELETE", auth: false });
  return unwrap<{ ok: boolean }>(res);
}

export async function fetchUsage(params: { store?: string; monthKey?: string; employeeId?: number; includeAll?: boolean }): Promise<UsageRow[]> {
  const p = new URLSearchParams();
  if (params.store) p.set("store", params.store);
  if (params.monthKey) p.set("monthKey", params.monthKey);
  if (params.employeeId) p.set("employeeId", String(params.employeeId));
  if (params.includeAll) p.set("includeAll", "true");
  const res = await apiFetch(`/discount/usage?${p.toString()}`, { auth: false });
  const d = await unwrap<{ usage: UsageRow[] }>(res);
  return d.usage;
}

export async function cancelUsage(id: number): Promise<{ ok: boolean }> {
  const res = await apiFetch(`/discount/usage/${id}`, { method: "DELETE", auth: false });
  return unwrap<{ ok: boolean }>(res);
}

export async function fetchStats(monthKey?: string): Promise<Stats> {
  const q = monthKey ? `?monthKey=${monthKey}` : "";
  const res = await apiFetch(`/discount/stats${q}`, { auth: false });
  return unwrap<Stats>(res);
}

export async function fetchSettings(): Promise<{ monthly_limit: number; discount_rate: number; discount_pin: string; reset_day: number }> {
  const res = await apiFetch("/discount/settings", { auth: false });
  return unwrap<{ monthly_limit: number; discount_rate: number; discount_pin: string; reset_day: number }>(res);
}

export async function saveSettings(data: { monthly_limit?: number; discount_rate?: number; discount_pin?: string; reset_day?: number }) {
  const res = await apiFetch("/discount/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    auth: false,
  });
  return unwrap<{ ok: boolean }>(res);
}

export async function bulkDeactivateEmployees(ids: number[]): Promise<{ count: number }> {
  const res = await apiFetch("/employees/bulk-deactivate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
    auth: false,
  });
  return unwrap<{ count: number }>(res);
}

export function formatPhone(phone: string) {
  const d = phone.replace(/\D/g, "");
  if (d.length === 11) return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  return phone;
}

export function formatAmount(n: number) {
  return n.toLocaleString("ko-KR") + "원";
}

export function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// ─── 관리자 인증 ────────────────────────────────────────────────
const ADMIN_TOKEN_KEY = "admin_token";
const ADMIN_EXPIRY_KEY = "admin_expiry";

export function getAdminToken(): string {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
}
export function setAdminSession(token: string, expiresAt: number) {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  sessionStorage.setItem(ADMIN_EXPIRY_KEY, String(expiresAt));
}
export function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_EXPIRY_KEY);
}
export function isAdminSessionValid(): boolean {
  const token = getAdminToken();
  const expiry = parseInt(sessionStorage.getItem(ADMIN_EXPIRY_KEY) ?? "0", 10);
  return !!token && Date.now() < expiry;
}

export async function adminLogin(password: string): Promise<{ ok: boolean; token?: string; expiresAt?: number; error?: string }> {
  const res = await apiFetch("/admin/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
    auth: false,
  });
  const json = await res.json();
  if (!json.data) return { ok: false, error: json.error?.message ?? "비밀번호가 틀렸습니다" };
  return { ok: true, token: json.data.token, expiresAt: json.data.expiresAt };
}

export async function adminVerify(): Promise<boolean> {
  const token = getAdminToken();
  if (!token) return false;
  try {
    const res = await apiFetch("/admin/verify", {
      auth: false,
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    return json.data?.valid === true;
  } catch {
    return false;
  }
}

export type MonthlyReportEmployee = {
  employeeId: number;
  name: string;
  phone: string;
  store: string;
  department: string | null;
  position: string | null;
  limit: number;
  totalPurchase: number;
  totalDiscount: number;
  totalPayment: number;
  remaining: number;
  usagePct: number;
  count: number;
};

export type MonthlyReport = {
  monthKey: string;
  discountRate: number;
  employees: MonthlyReportEmployee[];
  summary: {
    grandPurchase: number;
    grandDiscount: number;
    grandPayment: number;
    grandRemaining: number;
    totalActive: number;
  };
};

export async function fetchMonthlyReport(monthKey?: string): Promise<MonthlyReport> {
  const q = monthKey ? `?monthKey=${monthKey}` : "";
  const res = await apiFetch(`/discount/monthly-report${q}`, { auth: false });
  return unwrap<MonthlyReport>(res);
}

export async function downloadMonthlyReportCsv(monthKey?: string): Promise<void> {
  const params = new URLSearchParams();
  if (monthKey) params.set("monthKey", monthKey);
  const res = await apiFetch(`/discount/monthly-report-csv?${params.toString()}`, { auth: true });
  if (!res.ok) throw new Error("CSV 내보내기 실패");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const mk = monthKey ?? new Date().toISOString().slice(0, 7);
  a.href = url;
  a.download = `미미라인_월간정산_${mk}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function adminChangePassword(newPassword: string): Promise<{ ok: boolean; token?: string; expiresAt?: number; error?: string }> {
  const token = getAdminToken();
  const res = await apiFetch("/admin/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ newPassword }),
    auth: false,
  });
  const json = await res.json();
  if (!json.data?.token) return { ok: false, error: json.error?.message ?? "변경 실패" };
  return { ok: true, token: json.data.token, expiresAt: json.data.expiresAt };
}
